import { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, Loader2, Trash2, Package, MessageSquare, Clock, CheckCircle2, Inbox } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiErrorDetail } from "@/lib/api";
import Logo from "@/components/Logo";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BOOKING_STATUSES = ["pending", "confirmed", "in_progress", "completed", "cancelled"];
const ENQUIRY_STATUSES = ["new", "read", "resolved"];

const StatBox = ({ label, value, icon: Icon }) => (
  <div className="border border-foreground/15 p-6 flex items-center justify-between" data-testid={`stat-${label}`}>
    <div>
      <div className="text-4xl font-serif font-light">{value}</div>
      <div className="text-xs uppercase tracking-widest text-foreground/50 mt-1">{label}</div>
    </div>
    <Icon size={26} className="text-accent" />
  </div>
);

const AdminDashboard = () => {
  const { user, ready, logout } = useAuth();
  const navigate = useNavigate();
  const [tab, setTab] = useState("bookings");
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [enquiries, setEnquiries] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (ready && !user) navigate("/admin/login");
  }, [ready, user, navigate]);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [s, b, e] = await Promise.all([
        api.get("/admin/stats"),
        api.get("/admin/bookings"),
        api.get("/admin/enquiries"),
      ]);
      setStats(s.data); setBookings(b.data); setEnquiries(e.data);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { if (user) load(); }, [user, load]);

  const updateBooking = async (id, status) => {
    try {
      await api.patch(`/admin/bookings/${id}`, { status });
      toast.success("Booking updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const updateEnquiry = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      toast.success("Enquiry updated");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };
  const del = async (type, id) => {
    try {
      await api.delete(`/admin/${type}/${id}`);
      toast.success("Deleted");
      load();
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  const fmt = (iso) => (iso ? new Date(iso).toLocaleDateString("en-IN", { day: "numeric", month: "short", year: "numeric" }) : "—");

  if (!ready || !user) {
    return <div className="min-h-screen flex items-center justify-center"><Loader2 className="animate-spin" /></div>;
  }

  return (
    <div className="min-h-screen" data-testid="admin-dashboard">
      <header className="border-b border-foreground/15 sticky top-0 bg-background z-40">
        <div className="max-w-[1600px] mx-auto px-5 md:px-10 h-20 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Logo size="sm" />
            <span className="text-xs uppercase tracking-widest text-foreground/50 border-l border-foreground/20 pl-3">Admin Console</span>
          </div>
          <button onClick={() => { logout(); navigate("/admin/login"); }} data-testid="logout-btn"
            className="flex items-center gap-2 border border-foreground px-5 py-2.5 text-xs uppercase tracking-widest hover:bg-foreground hover:text-background transition-colors">
            <LogOut size={14} /> Logout
          </button>
        </div>
      </header>

      <main className="max-w-[1600px] mx-auto px-5 md:px-10 py-10">
        <div className="grid grid-cols-2 lg:grid-cols-5 gap-px bg-foreground/15 border border-foreground/15 mb-10">
          <div className="bg-background"><StatBox label="Total Bookings" value={stats?.total_bookings ?? "—"} icon={Package} /></div>
          <div className="bg-background"><StatBox label="Pending" value={stats?.pending_bookings ?? "—"} icon={Clock} /></div>
          <div className="bg-background"><StatBox label="Completed" value={stats?.completed_bookings ?? "—"} icon={CheckCircle2} /></div>
          <div className="bg-background"><StatBox label="Enquiries" value={stats?.total_enquiries ?? "—"} icon={MessageSquare} /></div>
          <div className="bg-background col-span-2 lg:col-span-1"><StatBox label="New Enquiries" value={stats?.new_enquiries ?? "—"} icon={Inbox} /></div>
        </div>

        <div className="flex gap-6 border-b border-foreground/15 mb-8">
          {[["bookings", "Bookings"], ["enquiries", "Enquiries"]].map(([id, label]) => (
            <button key={id} onClick={() => setTab(id)} data-testid={`tab-${id}`}
              className={`pb-4 text-sm uppercase tracking-widest transition-colors border-b-2 -mb-px ${tab === id ? "border-foreground text-foreground" : "border-transparent text-foreground/40 hover:text-foreground"}`}>
              {label}
            </button>
          ))}
        </div>

        {loading ? (
          <div className="py-24 flex justify-center"><Loader2 className="animate-spin" /></div>
        ) : tab === "bookings" ? (
          <div className="border border-foreground/15 overflow-x-auto" data-testid="bookings-table">
            {bookings.length === 0 ? <p className="p-8 text-foreground/50 font-light">No bookings yet.</p> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-foreground/15 text-left text-xs uppercase tracking-widest text-foreground/50">
                    <th className="p-4 font-medium">Customer</th>
                    <th className="p-4 font-medium">Service</th>
                    <th className="p-4 font-medium hidden md:table-cell">Address</th>
                    <th className="p-4 font-medium hidden lg:table-cell">Pickup</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {bookings.map((b) => (
                    <tr key={b.id} className="border-b border-foreground/10" data-testid={`booking-row-${b.id}`}>
                      <td className="p-4">
                        <div className="font-medium">{b.name}</div>
                        <div className="text-foreground/50">{b.phone}</div>
                      </td>
                      <td className="p-4">{b.service}<div className="text-foreground/40 text-xs">{b.service_type}</div></td>
                      <td className="p-4 hidden md:table-cell max-w-[220px] text-foreground/60">{b.address}</td>
                      <td className="p-4 hidden lg:table-cell text-foreground/60">{b.pickup_date || "—"}<div className="text-xs text-foreground/40">req {fmt(b.created_at)}</div></td>
                      <td className="p-4">
                        <Select value={b.status} onValueChange={(v) => updateBooking(b.id, v)}>
                          <SelectTrigger data-testid={`booking-status-${b.id}`} className="rounded-none h-9 w-[140px] border-foreground/20"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">
                            {BOOKING_STATUSES.map((s) => <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <button onClick={() => del("bookings", b.id)} data-testid={`booking-delete-${b.id}`} className="text-foreground/40 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        ) : (
          <div className="border border-foreground/15 overflow-x-auto" data-testid="enquiries-table">
            {enquiries.length === 0 ? <p className="p-8 text-foreground/50 font-light">No enquiries yet.</p> : (
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-foreground/15 text-left text-xs uppercase tracking-widest text-foreground/50">
                    <th className="p-4 font-medium">From</th>
                    <th className="p-4 font-medium">Message</th>
                    <th className="p-4 font-medium hidden lg:table-cell">Date</th>
                    <th className="p-4 font-medium">Status</th>
                    <th className="p-4 font-medium"></th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((e) => (
                    <tr key={e.id} className="border-b border-foreground/10" data-testid={`enquiry-row-${e.id}`}>
                      <td className="p-4">
                        <div className="font-medium">{e.name}</div>
                        <div className="text-foreground/50">{e.phone}</div>
                        {e.email && <div className="text-foreground/40 text-xs">{e.email}</div>}
                      </td>
                      <td className="p-4 max-w-[360px] text-foreground/70">{e.message}</td>
                      <td className="p-4 hidden lg:table-cell text-foreground/60">{fmt(e.created_at)}</td>
                      <td className="p-4">
                        <Select value={e.status} onValueChange={(v) => updateEnquiry(e.id, v)}>
                          <SelectTrigger data-testid={`enquiry-status-${e.id}`} className="rounded-none h-9 w-[130px] border-foreground/20"><SelectValue /></SelectTrigger>
                          <SelectContent className="rounded-none">
                            {ENQUIRY_STATUSES.map((s) => <SelectItem key={s} value={s} className="rounded-none">{s}</SelectItem>)}
                          </SelectContent>
                        </Select>
                      </td>
                      <td className="p-4">
                        <button onClick={() => del("enquiries", e.id)} data-testid={`enquiry-delete-${e.id}`} className="text-foreground/40 hover:text-destructive transition-colors"><Trash2 size={16} /></button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </div>
        )}
      </main>
    </div>
  );
};

export default AdminDashboard;
